'use client';

import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToastVariant, ToastPosition } from '@/hooks/use-toast';

/**
 * Toast variants with icon mapping and color styles
 */
const toastVariants = cva(
  'relative flex w-full items-center justify-between gap-3 rounded-lg border border-border-primary px-4 py-3 text-sm font-medium transition-all duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  {
    variants: {
      variant: {
        default:
          'border-border-primary bg-background-primary text-text-primary',
        success:
          'border-success-200 bg-success-50 text-success-900 dark:border-success-800 dark:bg-success-900 dark:text-success-50',
        error:
          'border-error-200 bg-error-50 text-error-900 dark:border-error-800 dark:bg-error-900 dark:text-error-50',
        destructive:
          'border-error-200 bg-error-50 text-error-900 dark:border-error-800 dark:bg-error-900 dark:text-error-50',
        warning:
          'border-warning-200 bg-warning-50 text-warning-900 dark:border-warning-800 dark:bg-warning-900 dark:text-warning-50',
        info: 'border-info-200 bg-info-50 text-info-900 dark:border-info-800 dark:bg-info-900 dark:text-info-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const toastIconVariants: Record<ToastVariant, React.ReactNode> = {
  default: null,
  success: <CheckCircle className="h-5 w-5 flex-shrink-0" />,
  error: <AlertCircle className="h-5 w-5 flex-shrink-0" />,
  destructive: <AlertCircle className="h-5 w-5 flex-shrink-0" />,
  warning: <AlertTriangle className="h-5 w-5 flex-shrink-0" />,
  info: <Info className="h-5 w-5 flex-shrink-0" />,
};

/**
 * Toast
 *
 * Main toast notification component.
 * Displays a dismissible notification with optional action button.
 *
 * @example
 * ```tsx
 * <Toast
 *   title="Success!"
 *   description="Your changes have been saved."
 *   variant="success"
 *   open={true}
 *   onOpenChange={setOpen}
 * >
 *   <ToastAction altText="Undo" onClick={() => console.log('Undo')}>
 *     Undo
 *   </ToastAction>
 * </Toast>
 * ```
 */
const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants> & {
    title?: React.ReactNode | undefined;
    description?: React.ReactNode | undefined;
    variant?: 'default' | 'success' | 'error' | 'destructive' | 'warning' | 'info' | null | undefined;
    position?: string | undefined;
    duration?: number | undefined;
    action?: { label: string; onClick: () => void } | undefined;
  }
>(
  (
    {
      className,
      variant = 'default',
      title,
      description,
      children,
      ...props
    },
    ref
  ) => {
    const icon = toastIconVariants[variant as ToastVariant];

    return (
      <ToastPrimitives.Root
        ref={ref}
        className={cn(toastVariants({ variant }), className)}
        {...props}
      >
        <div className="flex w-full items-start gap-3">
          {icon && <div className="mt-0.5">{icon}</div>}
          <div className="flex flex-1 flex-col gap-1">
            {title && (
              <ToastTitle>{title}</ToastTitle>
            )}
            {description && (
              <ToastDescription>{description}</ToastDescription>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {children}
          <ToastClose />
        </div>
      </ToastPrimitives.Root>
    );
  }
);
Toast.displayName = 'Toast';

/**
 * ToastTitle
 *
 * Title text for the toast notification.
 */
const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('font-semibold', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

/**
 * ToastDescription
 *
 * Description text for the toast notification.
 */
const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

interface ToastActionProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> {
  /** Text to display in the action button */
  label?: string;
}

/**
 * ToastAction
 *
 * Action button within the toast notification.
 * Prevents toast from closing when clicked.
 */
const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  ToastActionProps
>(({ className, label, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 items-center justify-center rounded-md px-3 text-xs font-medium ring-offset-background-primary transition-colors hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      className
    )}
    {...props}
  >
    {label || props.children}
  </ToastPrimitives.Action>
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

/**
 * ToastClose
 *
 * Close button for the toast notification.
 */
const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/50 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:hover:bg-red-400 group-[.destructive]:focus:ring-red-400',
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

/**
 * Toaster
 *
 * Radix Provider + Viewport wrapper. `children` (the individual `<Toast>`
 * items rendered by components/ui/toaster.tsx) must be passed in here so
 * they're descendants of `ToastPrimitives.Provider` — a `<Toast>` rendered
 * outside the Provider's subtree throws "Toast must be used within
 * ToastProvider" (Radix's context requirement), which is what happened
 * when the Provider+Viewport and the Toast items were previously rendered
 * as unrelated siblings.
 *
 * @example
 * ```tsx
 * <Toaster position="bottom-right">{toastItems}</Toaster>
 * ```
 */
const Toaster = ({
  position = 'bottom-right',
  children,
}: {
  position?: ToastPosition;
  children?: React.ReactNode;
}) => {
  const positionClasses: Record<ToastPosition, string> = {
    'top-right': 'top-0 right-0',
    'top-left': 'top-0 left-0',
    'bottom-right': 'bottom-0 right-0',
    'bottom-left': 'bottom-0 left-0',
  };

  return (
    <ToastPrimitives.Provider swipeDirection="right">
      {children}
      <ToastPrimitives.Viewport
        className={cn(
          'fixed z-[var(--z-index-toast)] flex max-w-md flex-col gap-3 p-4',
          positionClasses[position]
        )}
      />
    </ToastPrimitives.Provider>
  );
};
Toaster.displayName = 'Toaster';

export {
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  Toaster,
  toastVariants,
};
